import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CmsPageLayout from "../components/CmsPageLayout";
import CmsPageRoot from "../components/CmsPageRoot";
import CmsPageShell from "../components/CmsPageShell";
import SectionCard from "../components/SectionCard";
import ValidationSummary from "../components/ValidationSummary";
import StickyFormActions from "../components/StickyFormActions";
import PreviewJsonModal from "../components/PreviewJsonModal";
import {
  BilingualTableHeader,
  BilingualTextRow,
  BilingualImagePathRow,
} from "../components/BilingualFormFields";
import {
  CMS_LANGUAGES,
  CMS_PAGE_META,
  DEFAULT_TRANSLATIONS,
  TRANSLATION_SECTIONS,
} from "../constants";
import { validateTranslationsBilingual } from "../utils/validation";
import { applyCmsValidationFailure, scrollToFirstValidationError } from "../utils/validationUi";
import { fetchTranslations, saveTranslations, uploadMedia } from "../api/cmsApi";
import { useCmsAuth } from "../hooks/useCmsAuth";

const PAGE_META = CMS_PAGE_META.common;

const COMMON_SECTION_MAP = TRANSLATION_SECTIONS.map((section) => ({
  id: section.id,
  prefixes: section.keys.map((field) => field.key),
}));
const EN_ID = CMS_LANGUAGES.en.id;
const ES_ID = CMS_LANGUAGES.es.id;

function countBilingualErrors(errors, keys) {
  return keys.reduce((n, key) => {
    if (errors[`en.${key}`] || errors[`es.${key}`]) return n + 1;
    return n;
  }, 0);
}

function CommonLabelsPage() {
  const { token } = useCmsAuth();
  const [valuesEn, setValuesEn] = useState({ ...DEFAULT_TRANSLATIONS[EN_ID] });
  const [valuesEs, setValuesEs] = useState({ ...DEFAULT_TRANSLATIONS[ES_ID] });
  const [initialEn, setInitialEn] = useState({ ...DEFAULT_TRANSLATIONS[EN_ID] });
  const [initialEs, setInitialEs] = useState({ ...DEFAULT_TRANSLATIONS[ES_ID] });
  const [updatedAtEn, setUpdatedAtEn] = useState(null);
  const [updatedAtEs, setUpdatedAtEs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const dirtyEn = JSON.stringify(valuesEn) !== JSON.stringify(initialEn);
  const dirtyEs = JSON.stringify(valuesEs) !== JSON.stringify(initialEs);
  const dirty = dirtyEn || dirtyEs;

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrors({});
    try {
      const [enResult, esResult] = await Promise.all([
        fetchTranslations(token, EN_ID),
        fetchTranslations(token, ES_ID),
      ]);
      const enMerged = { ...DEFAULT_TRANSLATIONS[EN_ID], ...(enResult.values || {}) };
      const esMerged = { ...DEFAULT_TRANSLATIONS[ES_ID], ...(esResult.values || {}) };
      setValuesEn(enMerged);
      setValuesEs(esMerged);
      setInitialEn(enMerged);
      setInitialEs(esMerged);
      setUpdatedAtEn(enResult.updatedAt);
      setUpdatedAtEs(esResult.updatedAt);
    } catch (err) {
      setValuesEn({ ...DEFAULT_TRANSLATIONS[EN_ID] });
      setValuesEs({ ...DEFAULT_TRANSLATIONS[ES_ID] });
      setInitialEn({ ...DEFAULT_TRANSLATIONS[EN_ID] });
      setInitialEs({ ...DEFAULT_TRANSLATIONS[ES_ID] });
      toast.error("Could not load from server. Restore CMS data in MongoDB.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const trimValues = (values) => {
    const trimmed = {};
    Object.keys(values).forEach((key) => {
      trimmed[key] = typeof values[key] === "string" ? values[key].trim() : values[key];
    });
    return trimmed;
  };

  const handleSave = async () => {
    const validationErrors = validateTranslationsBilingual(valuesEn, valuesEs);
    if (Object.keys(validationErrors).length) {
      applyCmsValidationFailure({
        validationErrors,
        setErrors,
        sectionPrefixMap: COMMON_SECTION_MAP,
        toastFn: (message) => toast.error(message),
      });
      return;
    }

    setSaving(true);
    try {
      const enTrimmed = trimValues(valuesEn);
      const esTrimmed = trimValues(valuesEs);
      const [enResult, esResult] = await Promise.all([
        saveTranslations(token, EN_ID, enTrimmed),
        saveTranslations(token, ES_ID, esTrimmed),
      ]);
      setValuesEn(enTrimmed);
      setValuesEs(esTrimmed);
      setInitialEn(enTrimmed);
      setInitialEs(esTrimmed);
      setErrors({});
      setUpdatedAtEn(enResult.updatedAt || new Date().toISOString());
      setUpdatedAtEs(esResult.updatedAt || new Date().toISOString());
      toast.success("Saved — live on the website (English & Spanish).");
    } catch (err) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const updateEn = (key, value) => {
    setValuesEn((prev) => ({ ...prev, [key]: value }));
    if (errors[`en.${key}`]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`en.${key}`];
        return next;
      });
    }
  };

  const updateEs = (key, value) => {
    setValuesEs((prev) => ({ ...prev, [key]: value }));
    if (errors[`es.${key}`]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`es.${key}`];
        return next;
      });
    }
  };

  const handleImageUpload = async (lang, fieldKey, file) => {
    setUploadingKey(`${lang}.${fieldKey}`);
    try {
      const publicUrl = await uploadMedia(token, file);
      if (publicUrl) {
        if (lang === "en") {
          updateEn(fieldKey, publicUrl);
        } else {
          updateEs(fieldKey, publicUrl);
        }
        toast.success("Image uploaded.");
      } else {
        toast.error("No URL returned from upload.");
      }
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploadingKey(null);
    }
  };

  const renderField = (field) => {
    if (field.kind === "imagePath") {
      return (
        <BilingualImagePathRow
          key={field.key}
          label={field.label}
          fieldKey={field.key}
          srcEn={valuesEn[field.key]}
          srcEs={valuesEs[field.key]}
          onUploadEn={(f) => handleImageUpload("en", field.key, f)}
          onUploadEs={(f) => handleImageUpload("es", field.key, f)}
          srcErrorEn={errors[`en.${field.key}`]}
          srcErrorEs={errors[`es.${field.key}`]}
          uploadingEn={uploadingKey === `en.${field.key}`}
          uploadingEs={uploadingKey === `es.${field.key}`}
        />
      );
    }

    return (
      <BilingualTextRow
        key={field.key}
        label={field.label}
        fieldKey={field.key}
        valueEn={valuesEn[field.key]}
        valueEs={valuesEs[field.key]}
        onChangeEn={(v) => updateEn(field.key, v)}
        onChangeEs={(v) => updateEs(field.key, v)}
        errorEn={errors[`en.${field.key}`]}
        errorEs={errors[`es.${field.key}`]}
        maxLength={field.maxLength || 500}
        type={field.type || "text"}
        multiline={!!field.multiline}
        rows={field.multiline ? 2 : 1}
      />
    );
  };

  return (
    <CmsPageRoot>
      <CmsPageLayout
        actions={
          <StickyFormActions
            dirty={dirty}
            saving={saving}
            onSave={handleSave}
            onPreview={() => setPreviewOpen(true)}
          />
        }
      >
      <CmsPageShell
        pageIcon={PAGE_META.pageIcon}
        pageIconSrc={PAGE_META.tabIcon}
        title={PAGE_META.title}
        subtitle={PAGE_META.subtitle}
        dirtyEn={dirtyEn}
        dirtyEs={dirtyEs}
        updatedAtEn={updatedAtEn}
        updatedAtEs={updatedAtEs}
        loading={loading}
        skeletonPage="common"
      >
        <ValidationSummary
          errors={errors}
          onJumpToFirst={() =>
            scrollToFirstValidationError(errors, { sectionPrefixMap: COMMON_SECTION_MAP })
          }
        />

        {TRANSLATION_SECTIONS.map((section) => (
          <SectionCard
            key={section.id}
            id={section.id}
            title={section.title}
            description={section.description}
            errorCount={countBilingualErrors(errors, section.keys.map((k) => k.key))}
            collapsible={section.id !== "site" && section.id !== "seo"}
            defaultOpen={section.id === "site" || section.id === "seo" || section.id === "navigation"}
          >
            <BilingualTableHeader />
            {section.keys.map((field) => renderField(field))}
          </SectionCard>
        ))}
      </CmsPageShell>
      </CmsPageLayout>

      <PreviewJsonModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        title="Translations payload"
        payload={{ en: valuesEn, es: valuesEs }}
      />
    </CmsPageRoot>
  );
}

export default CommonLabelsPage;
